import React, { useState, useRef, useEffect } from 'react';
import { AI_CHAT_SYSTEM_PROMPT } from '../data';

const SUGGESTED = [
  "What makes Mekhal stand out?",
  "Tell me about Finide",
  "What roles is he open to?",
  "What's his data engineering background?",
];

export default function AIChat() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi — I'm Mekhal's portfolio AI. Ask me anything about his experience, projects, or what he's building. I'll respond as Mekhal would." }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const history = messages.filter(m => m.role !== 'system').map(m => ({ role: m.role, content: m.content }));
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: AI_CHAT_SYSTEM_PROMPT,
          messages: [...history, { role: 'user', content: userMsg }],
        }),
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "I'm not sure how to answer that — feel free to reach out directly at mekhalrajr@gmail.com.";
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Something went wrong. Try reaching Mekhal directly at mekhalrajr@gmail.com." }]);
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', bottom: '80px', right: '24px', zIndex: 800 }}>
      {/* Toggle button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '52px', height: '52px', borderRadius: '50%',
          background: 'var(--ink)', border: 'none', cursor: 'crosshair',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
          transition: 'transform 0.2s',
          transform: open ? 'scale(0.9)' : 'scale(1)',
          marginLeft: 'auto',
        }}
      >
        <span style={{ fontSize: '20px' }}>{open ? '×' : '💬'}</span>
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{
          position: 'absolute', bottom: '64px', right: 0,
          width: 'min(360px, calc(100vw - 32px))', background: 'var(--bg)',
          border: '1px solid rgba(26,26,26,0.12)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
          display: 'flex', flexDirection: 'column',
          maxHeight: '500px',
        }}>
          {/* Header */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(26,26,26,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '16px', fontWeight: 600, color: 'var(--ink)' }}>Ask Mekhal</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.15em', color: 'var(--ink-light)', textTransform: 'uppercase' }}>AI · Powered by Claude</div>
            </div>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px rgba(34,197,94,0.5)' }} />
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '200px', maxHeight: '300px' }}>
            {messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                background: m.role === 'user' ? 'var(--ink)' : 'rgba(26,26,26,0.04)',
                padding: '10px 14px',
                fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 300,
                color: m.role === 'user' ? 'var(--bg)' : 'var(--ink)',
                lineHeight: 1.6,
              }}>
                {m.content}
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf: 'flex-start', padding: '10px 14px', background: 'rgba(26,26,26,0.04)', fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--ink-light)', letterSpacing: '0.1em' }}>
                Thinking...
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggested questions */}
          {messages.length <= 1 && (
            <div style={{ padding: '0 20px 12px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {SUGGESTED.map((q, i) => (
                <button key={i} onClick={() => send(q)} style={{
                  fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.1em',
                  color: 'var(--ink-mid)', background: 'none', border: '1px solid rgba(26,26,26,0.15)',
                  padding: '5px 10px', cursor: 'crosshair', textAlign: 'left',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => { e.target.style.borderColor = 'var(--ink)'; e.target.style.color = 'var(--ink)'; }}
                  onMouseLeave={e => { e.target.style.borderColor = 'rgba(26,26,26,0.15)'; e.target.style.color = 'var(--ink-mid)'; }}
                >{q}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(26,26,26,0.08)', display: 'flex', gap: '10px' }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
              placeholder="Ask anything..."
              style={{
                flex: 1, fontFamily: 'var(--font-body)', fontSize: '13px',
                border: '1px solid rgba(26,26,26,0.15)', padding: '8px 12px',
                background: 'transparent', color: 'var(--ink)', outline: 'none',
              }}
            />
            <button onClick={() => send()} style={{
              fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.15em',
              color: 'var(--bg)', background: 'var(--ink)', border: 'none',
              padding: '0 16px', cursor: 'crosshair', textTransform: 'uppercase',
              transition: 'opacity 0.2s',
            }}
              onMouseEnter={e => e.target.style.opacity = '0.8'}
              onMouseLeave={e => e.target.style.opacity = '1'}
            >→</button>
          </div>
        </div>
      )}
    </div>
  );
}
